/** @jsx jsx */

import { jsx } from '@emotion/react';
import { get, orderBy, set, uniq } from "lodash";
import { Org } from "../../_shared/interfaces/org";
import { _Location, Location } from "../../_shared/interfaces/location";
import { DatePicker, Form, Input, message, Select } from "antd";
import { useSelector } from "react-redux";
import { ChangeEvent, FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { Model } from "../../_shared/interfaces/model";
import { getOrgs } from "../../_shared/services/manage-orgs.service";
import { createLocation, getLocations } from "../../_shared/services/manage-locations.service";
import { getModels } from "../../_shared/services/manage-models.service";
import { userHasRole } from "../../utils";
import { AppState } from "../../app.state";
import { _EquipmentRecord, Equipment } from "../../_shared/interfaces/equipment";
import { User } from "../../_shared/interfaces/user";
import { deviceInfo, DeviceInfoResult, exists } from "../../_shared/services/hardware.service";
import { AddLocationModal } from '../add-location-modal.component';
import moment, { Moment } from 'moment';
import './a1-registration.css';
import { AxiosResponse } from 'axios';
import { Button } from '../../_shared/button';
import { WithForm } from '../../_shared/form';

interface OrgLocationProps {
  orgs?: Org[];
  orgId: string;
  setOrg: (orgId: string) => void;
  locations?: Location[];
  locationId: string;
  setLocation: (locationId: string) => void;
  disabled?: boolean;
  form?: any;
  user: User;
  locationAdded: (locationId: string) => void;
  showOrg: boolean;
};

const OrgLocationComponent: React.FC<OrgLocationProps> = props => {
  const { orgs, orgId, setOrg, locations, locationId, setLocation, form, user, locationAdded, showOrg } = props;
  const disabled = props.disabled || false;

  const locDisabled = locations === undefined || locations.length === 0 || !orgId;

  const [locationModal, setLocationModal] = useState({});

  const addLocation = () => {
    let form: any;

    const close = () => {
      setLocationModal({});
    }

    setLocationModal({
      title: 'Add Location',
      visible: true,
      formProps: {
        registerForm: (f: any) => form = f,
      },
      footer: null,
      onCancel: () => {
        close()
      },
      onSubmit: () => {
        form.validateFieldsAndScroll(async (err: any, vals: any) => {
          if (err) {
            return;
          }

          try {
            // AER-1619: antd v5 returns nested form fields (e.g. `address.address`)
            // as flat dotted keys rather than the v3 nested-object shape. Re-nest
            // them before constructing the location, otherwise the saved record
            // ends up with literal "address.address" keys and an empty `address`.
            const nested = Object.keys(vals).reduce((acc, k) => set(acc, k, vals[k]), {} as any);
            const loc = new _Location(nested);
            loc.orgId = orgId;
            loc.createdBy = user.email;
            loc.updatedBy = user.email;
            const new_loc = await createLocation(loc);
            close();
            locationAdded(new_loc.id);
          } catch (err) {
            console.error(err);
          }
        });
      }
    })
  }

  const filteredOrgs = orgs !== undefined
    ? orderBy(orgs.filter(o => o.orgType === 1).map(o => ({ title: o.name, value: o.id })), 'title', 'asc')
    : [];

  const orgDecoratorOptions = {
    rules: [{ required: true, message: 'Dental Organization is required' }],
    initialValue: orgId,
  };

  const locDecoratorOptions = {
    rules: [{ required: true, message: 'Location is required' }],
    initialValue: locationId,
  };

  const filteredLocations = useMemo(() => {
    if (locations === undefined) return [];
    return orderBy(locations.filter(l => l.orgId !== undefined && l.orgId === orgId), 'name', 'asc');
  }, [locations, orgId]);

  // AER-1643 sweep: ported from AntD3 `getFieldDecorator(...)` (removed in AntD6) to
  // AntD6 `<Form.Item name rules initialValue>` with the control as a direct child.
  return (<div className="org-location-container">
    <Form.Item label='Dental Organization' key='fi-org' name='orgId' rules={orgDecoratorOptions.rules} initialValue={orgDecoratorOptions.initialValue}>
      <Select
        showSearch
        filterOption={(inp: string, option: any) => get(option, 'props.children', '').toUpperCase().indexOf(inp.toUpperCase()) !== -1}
        onChange={(v: string) => {
          setOrg(v);
          form.setFieldsValue({ orgId: v });
        }}
        disabled={disabled || !showOrg}
      >
        <Select.Option key={0} value=''>{' '}</Select.Option>
        {filteredOrgs.map((o, i) => (
          <Select.Option key={i + 1} value={o.value}>
            {o.title}
          </Select.Option>))}
      </Select>
    </Form.Item>
    <div className='locationRow'>
      <Form.Item className='locationFormItem' label='Location' style={{}} key='fi-loc' name='locationId' rules={locDecoratorOptions.rules} initialValue={locDecoratorOptions.initialValue}>
        <Select
          onChange={(v: string) => {
            form.setFieldsValue({ locationId: v });
            setLocation(v);
          }}
          disabled={locDisabled || disabled}
        >
          <Select.Option key={0} value=''>{' '}</Select.Option>
          {filteredLocations.map((o, i) => (
            <Select.Option key={i + 1} value={o.id}>
              {o.name}
            </Select.Option>))}
        </Select>
      </Form.Item>
      <div className="addLocationButton">
        <Button disabled={locDisabled} icon="plus" onClick={addLocation} title="" />
      </div>
    </div>
    <AddLocationModal {...locationModal} />
  </div>);
};

interface IEquipmentConfig {
  type: "aerasone_compressor" | "aerasone_vacuum" | "";
  modelId: string;
  serial: string;
  eq: number;
  installDate?: Moment;
}

interface IEquipmentConfigProps extends IEquipmentConfig {
  setType: (type: "aerasone_compressor" | "aerasone_vacuum" | "") => void;
  models: Model[];
  setModelId: (modelId: string) => void;
  serial: string;
  setSerial: (serial: string) => void;
  form: any;
  setInstallDate: (installDate: Moment) => void;
};

const EquipmentConfigComponent: React.FC<IEquipmentConfigProps> = (props => {
  const {
    type, setType, models, modelId, setModelId, serial, setSerial, installDate, setInstallDate, form, eq,
  } = props;

  const [manufacturer, setManufacturer] = useState('');

  const manufacturers = useMemo(() => {
    return uniq(models.map(m => m.manufacturer)).sort();
  }, [models]);

  const displayModels = useMemo(() => manufacturer === ''
    ? models
    : models.filter(m => m.manufacturer === manufacturer), [manufacturer]);

  // AER-1643 sweep: ported from AntD3 `getFieldDecorator` to AntD6 `<Form.Item name>`.
  return <div className='equipment-config-container'>
    <div className='a1-purple-subheader'>Equipment {eq}</div>
    <Form.Item label='Equipment Type' key={'fi-type' + eq} name={`type_${eq}`} initialValue={type}>
      <Select
        onChange={(value: string) => {
          form.setFieldsValue({ [`type_${eq}`]: value });
          setType(value as "aerasone_compressor" | "aerasone_vacuum" | "");
          if (value !== type) {
            form.setFieldsValue({ [`mfr_${eq}`]: '' });
            form.setFieldsValue({ [`modelId_${eq}`]: '' });
            form.setFieldsValue({ [`serial_${eq}`]: '' });
            form.setFieldsValue({ [`installDate_${eq}`]: undefined });
          }
        }}
      >
        <Select.Option key={0} value="">None Attached</Select.Option>
        <Select.Option key={1} value="aerasone_compressor">Compressor</Select.Option>
        <Select.Option key={2} value="aerasone_vacuum">Vacuum</Select.Option>
      </Select>
    </Form.Item>
    <Form.Item label='Manufacturer' key={'fi-mfr' + eq} name={`mfr_${eq}`} initialValue={manufacturer} rules={[{ required: !!type, message: "Manufacturer is required" }]}>
      <Select
        onChange={(value: string) => {
          form.setFieldsValue({ [`mfr_${eq}`]: value });
          setManufacturer(value);
        }}
        disabled={type === ''}
      >
        <Select.Option key={0} value="">{' '}</Select.Option>
        {manufacturers.map((m, i) => (
          <Select.Option key={i + 1} value={m}>{m}</Select.Option>
        ))}
      </Select>
    </Form.Item>
    <Form.Item label='Model' key={'fi-modelid' + eq} name={`modelId_${eq}`} rules={[{ required: !!type, message: "Model is required" }]} validateTrigger='onBlur' initialValue={modelId}>
      <Select
        disabled={manufacturer === '' || type === ''}
        onChange={(value: string) => {
          form.setFieldsValue({ [`modelId_${eq}`]: value });
          setModelId(value);
        }}
      >
        <Select.Option key={0} value={""}>{' '}</Select.Option>
        {displayModels.map((m, i) => (
          <Select.Option key={i + 1} value={m.id}>{m.id}</Select.Option>
        ))}
      </Select>
    </Form.Item>
    <Form.Item label='Equipment Serial Number' key={'fi-serial' + eq} name={`serial_${eq}`} rules={[{ required: !!type, message: "Serial # is required" }]} validateTrigger='onBlur' initialValue={serial}>
      <Input
        onChange={(event: ChangeEvent<HTMLInputElement>) => {
          form.setFieldsValue({ [`serial_${eq}`]: event.target.value });
          setSerial(event.target.value);
        }}
        disabled={type === ''}
      />
    </Form.Item>
    <Form.Item label='Install Date' key={'fi-installdate' + eq} name={`installDate_${eq}`} initialValue={installDate}>
      <DatePicker
        disabled={type === ''}
        onChange={((date: moment.Moment, dateString: string) => {
          setInstallDate(date);
          form.setFieldsValue({ [`installDate_${eq}`]: date });
        }) as any}
      />
    </Form.Item>
  </div>;
});

const blankConfig = {
  type: "" as "aerasone_compressor" | "aerasone_vacuum" | "",
  modelId: "",
  serial: "",
};


export interface A1RegistrationProps {
  register: (
    deviceId: string,
    equipment: (Equipment | undefined)[],
    models: (Model | undefined)[],
  ) => void;
  form?: any;
};

const _A1RegistrationForm: React.FC<A1RegistrationProps> = props => {
  const { form, register } = props;
  // AER-1643 sweep: the connected AntD6 Form wrapper supplied by WithForm. Rendering
  // our `<Form.Item name>` children inside it wires them to the same form instance that
  // `form.validateFieldsAndScroll`/`getFieldsError` operate on.
  const WForm = form.Form;
  const user: any = useSelector(s => get(s, 'auth.user'));
  const [loading, setLoading] = useState(true);

  const [userOrg, setUserOrg] = useState(undefined as Org | undefined);
  const [orgs, setOrgs] = useState([] as Org[]);
  const [locations, setLocations] = useState([] as Location[]);
  const [orgId, setOrgId] = useState('');
  const [locationId, setLocationId] = useState('');

  const [compressorModels, setCompressorModels] = useState([] as Model[]);
  const [vacuumModels, setVacuumModels] = useState([] as Model[]);

  const [controllerSN, setControllerSN] = useState('');
  const [equipment, setEquipment] = useState<IEquipmentConfig[]>([
    {
      ...blankConfig,
      eq: 0,
    },
    {
      ...blankConfig,
      eq: 1,
    },
  ]);

  const [error, setError] = useState('');

  const loadItems = async () => {
    try {
      setLoading(true);
      const [loadOrgs, loadLocations, loadModels] = await Promise.all([
        getOrgs(),
        getLocations(),
        getModels(),
      ]);

      const userOrg = loadOrgs.find(o => o.id === user.orgId) as Org;
      setOrgs(userOrg && userOrg.orgType == 0 ? loadOrgs.filter(o => o.serviceOrgId == userOrg.id) : loadOrgs);
      setLocations(loadLocations);
      setOrgId(userOrg !== undefined ? userOrg.id : '');
      if (userOrg !== undefined) {
        form.setFieldsValue({ orgId: userOrg.id });
      }
      setCompressorModels(loadModels.filter(m => m.type === "aerasone_compressor"));
      setVacuumModels(loadModels.filter(m => m.type === "aerasone_vacuum"));
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  }

  const changeEquipment = (eq: number, prop: string, value: any) => {
    const newEquipment = [...equipment];
    if (prop === 'type' && get(newEquipment[eq], 'type', undefined) !== value) {
      newEquipment[eq] = { type: value, modelId: '', eq, serial: '' };
    } else {
      set(newEquipment[eq], prop, value);
    }
    console.log(`setting equipment: ${JSON.stringify(newEquipment)}`);
    setEquipment(newEquipment);
    setError('');
  }

  useEffect(() => {
    loadItems();
  }, []);

  const showOrg = userOrg !== undefined && userOrg.orgType == 0 || userHasRole([0], { auth: { user } } as any)

  const onSubmit = useCallback(async (e: MouseEvent | FormEvent) => {
    setError('');

    // AER-1643 sweep: AntD6 `validateFields` is Promise-based and ignores a node-style
    // callback (the old call would hang forever). Use the WithForm `validateFieldsAndScroll`
    // shim, which resolves the callback with (null, vals) on success / (err, {}) on failure.
    const { validateFieldsAndScroll } = form;

    e.preventDefault();
    try {
      const validateResult: { errs: any, vals: any } = await new Promise((resolve, reject) => {
        try {
          validateFieldsAndScroll((errs: any, vals: any) => {
            resolve({ errs, vals });
          });
        } catch (err) {
          reject(err);
        }
      });

      if (validateResult.errs) {
        console.log('validation failed');
        return;
      }

      if (!equipment[0].type && !equipment[1].type) {
        console.log('no equipment selected');
        setError("At least one piece of equipment must be connected.");
        return;
      }

      // check if equipment already exists for this controller
      const existsResult = await exists(controllerSN);
      if (existsResult.data.exists) {
        const error = "Equipment already exists for this controller";
        message.error(error);
        console.log('controller SN already exists');
        return;
      }

      // validate Particle serial number and get device ID
      let deviceIdResult: AxiosResponse<DeviceInfoResult, any> | undefined = undefined;
      try {
        deviceIdResult = await deviceInfo(controllerSN);
      } catch (err) {
        console.log(`Caught exception on deviceId: ${JSON.stringify(err)}`);
      }

      if (!deviceIdResult || deviceIdResult.status !== 200 || !deviceIdResult.data.ok) {
        const error = 'Failed to retrieve device info. Please check Controller Serial #.';
        message.error(error);
        console.log("Couldn't fetch controller info" + deviceIdResult ? `: ${JSON.stringify(deviceIdResult!.data)}` : '');
        setError(error);
        return;
      }

      const deviceId = deviceIdResult.data.device_id;
      const platform = deviceIdResult.data.platform;

      // build Equipment records and pass them up
      const records = equipment.map((e, i) => {
        if (e.type) {
          return new _EquipmentRecord({
            deviceId: `${deviceId}_${i}`,
            controllerSerial: controllerSN,
            equipmentSN: e.serial,
            modelId: e.modelId,
            locationId: locationId,
            dentalOrgId: orgId,
            installDate: e.installDate ? e.installDate.toISOString() : undefined,
            name: e.serial,
            showOnDash: true,
            platform,
          }) as Equipment;
        } else return undefined;
      });

      // pass instructions along
      const models = equipment.map((e, i) => {
        if (e.type === "aerasone_compressor") {
          return compressorModels.find(m => m.id === e.modelId);
        } else if (e.type === "aerasone_vacuum") {
          return vacuumModels.find(m => m.id === e.modelId);
        } else return undefined;
      });
      register(deviceId, records, models);
    } catch (err) {
      setError(err.Message);
      console.log(`Caught exception: ${JSON.stringify(err)}`);
    } finally {
    }

  }, [form, equipment, controllerSN, register, locationId, orgId, compressorModels, vacuumModels]);

  // AER-1667 (R1): the Continue button is no longer disabled by field-validation
  // state (the former hasFieldError() gate was removed below); it stays gated only
  // by the in-flight `loading` guard. Field rules still validate on blur/submit.

  return (<div className="a1-registration-container">
    <p className="a1-purple-subheader">Equipment Registration</p>
    <WForm>
      {<OrgLocationComponent
        orgs={orgs}
        orgId={orgId}
        setOrg={setOrgId}
        locations={locations}
        locationId={locationId}
        setLocation={l => {
          setLocationId(l);
        }}
        form={form}
        disabled={loading}
        user={user}
        showOrg={showOrg}
        locationAdded={async (locationId: string) => {
          try {
            const locations = await getLocations();
            setLocations(locations);
            setLocationId(locationId);
          } catch (e) {
            console.log(e);
          }
        }}
      />}
      <Form.Item label='Aeras Intel Device Serial Number' name='controllerSN' rules={[{ required: true, message: 'Controller Serial # is required.' }]} validateTrigger='onBlur' initialValue={controllerSN}>
        <Input
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            form.setFieldsValue({ controllerSN: event.target.value });
            setControllerSN(event.target.value);
          }} />
      </Form.Item>
      <div className='equipment-configs-container'>
        {equipment.map((e, i) => (
          <EquipmentConfigComponent
            key={`ecc_${i}`}
            type={e.type}
            setType={t => changeEquipment(i, 'type', t)}
            models={e.type === 'aerasone_compressor'
              ? compressorModels
              : e.type === 'aerasone_vacuum'
                ? vacuumModels
                : []
            }
            setModelId={m => changeEquipment(i, 'modelId', m)}
            modelId={e.modelId}
            eq={i}
            serial={e.serial}
            setSerial={s => changeEquipment(i, 'serial', s)}
            form={form}
            installDate={e.installDate}
            setInstallDate={s => changeEquipment(i, 'installDate', s)}
          />
        ))}
      </div>
      <div className='a1-error'>{error}</div>
      <Button type="primary" htmlType='submit' onClick={onSubmit} disabled={loading}>Continue</Button>
    </WForm>
  </div>);
};

export const A1RegistrationForm = WithForm(_A1RegistrationForm) as unknown as React.FC<A1RegistrationProps>;
